import { buildMealPlanText } from "./mealPlanText";
import type { PlanMeal } from "./mealPlanShape";

/**
 * "💬 Talk to Kin about this plan" — issue #1450.
 *
 * The Today tab already has the shape: `onAskKin(context)` switches to chat and
 * SENDS the context as her message, so Kin's reply is already arriving when she
 * lands there. This module builds that context for a meal plan. It is one
 * formatter for the week (`buildMealPlanText`, standing rule 10) with an intro
 * line on top and a machine-readable run marker on the bottom.
 *
 * The marker is for the follow-on issues (#1451 reads the plan by tool, #1452
 * commits it). Nothing renders it: `stripMealPlanRunTag` runs on the user
 * bubble and on the assistant text in ChatMessages, and a test pins both.
 */

export const MEAL_PLAN_RUN_TAG_PREFIX = "[MEAL_PLAN_RUN:";

/** `[MEAL_PLAN_RUN:<uuid>]` on its own line, or inline. */
const MEAL_PLAN_RUN_TAG_RE = /\s*\[MEAL_PLAN_RUN:[0-9a-f-]{36}\]\s*/gi;

export function mealPlanRunTag(runId: string): string {
  return `${MEAL_PLAN_RUN_TAG_PREFIX}${runId}]`;
}

/** Remove every run marker. Collapses the whitespace around it so a marker on
 *  its own line does not leave a blank one behind. The fast path is the same
 *  case-insensitive test as the matcher, and only the TAIL is trimmed: leading
 *  whitespace in her message is hers (Copilot, PR #1477). */
const MEAL_PLAN_RUN_TAG_PRESENT_RE = /\[MEAL_PLAN_RUN:/i;
export function stripMealPlanRunTag(text: string): string {
  if (!text || !MEAL_PLAN_RUN_TAG_PRESENT_RE.test(text)) return text;
  return text.replace(MEAL_PLAN_RUN_TAG_RE, "\n").replace(/\n{3,}/g, "\n\n").trimEnd();
}

/** Read the run id back out of a message, or null. First marker wins. */
export function readMealPlanRunTag(text: string): string | null {
  const m = /\[MEAL_PLAN_RUN:([0-9a-f-]{36})\]/i.exec(text || "");
  return m ? m[1] : null;
}

export const MEAL_PLAN_CHAT_INTRO_DRAFT =
  "Here's the draft meal plan Kin made for this week. I want to talk it through and change some nights.";
export const MEAL_PLAN_CHAT_INTRO_COMMITTED =
  "Here's this week's meal plan from Kin. I want to talk it through.";

/** The footer buildMealPlanText adds for a text she sends as herself. Not
 *  something she would say to Kin, so the chat context drops it. */
const SEND_FOOTER = "Sent from my Kin app 💜";

export interface MealPlanChatContextInput {
  runId: string;
  meals: PlanMeal[];
  weekSummary?: string | null;
  /** true while the plan is still a draft she is reviewing. */
  pending: boolean;
}

/**
 * The message sent to Kin. Empty string when the plan has nothing plannable
 * (every night removed, or no meals), so the caller can hide the button.
 */
export function buildMealPlanChatContext(input: MealPlanChatContextInput): string {
  const body = buildMealPlanText({ meals: input.meals, weekSummary: input.weekSummary });
  if (!body) return "";
  const lines = body.split("\n");
  while (lines.length > 0 && (lines[lines.length - 1].trim() === SEND_FOOTER || lines[lines.length - 1].trim() === "")) {
    lines.pop();
  }
  const intro = input.pending ? MEAL_PLAN_CHAT_INTRO_DRAFT : MEAL_PLAN_CHAT_INTRO_COMMITTED;
  return [intro, "", ...lines, "", mealPlanRunTag(input.runId)].join("\n");
}
